"use client";

// src/app/error.tsx
// Root segment error boundary: a render throw anywhere without a
// closer boundary degrades to a retry card inside the root layout. Only the
// error's name, digest and redacted route reach PostHog, never the message.
import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { captureErrorBoundary } from "@/lib/posthog/error-events";
import { readTranslated } from "@/lib/posthog/translation";

export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [translated] = useState(() => readTranslated());

  useEffect(() => {
    captureErrorBoundary(error, window.location.pathname, translated);
  }, [error, translated]);

  return (
    <div className="flex min-h-[60vh] flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          This page failed to load. You can try again or go back home.
        </p>
      </div>
      <div className="flex gap-2">
        <Button
          className="min-h-11 bg-brand text-white hover:bg-brand-ink focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          onClick={reset}
        >
          Try again
        </Button>
        <Button
          asChild
          variant="outline"
          className="min-h-11 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        >
          <Link href="/">Go to home</Link>
        </Button>
      </div>
    </div>
  );
}
